import {
  DEFAULT_QR_ASSETS,
  DEFAULT_QR_COLORS,
  DEFAULT_QR_NAME,
  DEFAULT_QR_TEXT,
  DEFAULT_QR_TITLE,
  QR_TEXT_KEYS,
} from "./qrWebsiteConfigBootstrap"
import { getDeepValue, setDeepValue } from "./formUtils"

const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{6})$/

export const QR_BUILDER_TEMPLATE_ID = "qr-website"

const LEGACY_QR_TEMPLATE_IDS = new Set(["sign-qr-ordering"])

const resolveQrTemplateId = templateId => {
  if (templateId === QR_BUILDER_TEMPLATE_ID || LEGACY_QR_TEMPLATE_IDS.has(templateId)) {
    return QR_BUILDER_TEMPLATE_ID
  }
  return QR_BUILDER_TEMPLATE_ID
}

const normalizeHexColor = (value, fallback) => {
  if (typeof value !== "string") return fallback
  const trimmed = value.trim()
  return HEX_COLOR_REGEX.test(trimmed) ? trimmed : fallback
}

const normalizeTextEntry = (value, fallback) => {
  const input = value && typeof value === "object" ? value : {}
  return {
    section:
      typeof input.section === "string"
        ? input.section.trim()
        : fallback.section || "",
    en: typeof input.en === "string" ? input.en : fallback.en || "",
    ar: typeof input.ar === "string" ? input.ar : fallback.ar || "",
  }
}

const normalizeTextMap = (value, defaults = DEFAULT_QR_TEXT) => {
  const input = value && typeof value === "object" ? { ...value } : {}
  const normalized = {}

  if (
    input["orders.paymentMethods.signRewards"] &&
    !input["orders.paymentMethods.loyaltyRewards"]
  ) {
    input["orders.paymentMethods.loyaltyRewards"] =
      input["orders.paymentMethods.signRewards"]
  }

  QR_TEXT_KEYS.forEach(key => {
    normalized[key] = normalizeTextEntry(input[key], defaults[key] || {})
  })

  return normalized
}

export const createEmptyQrWebsiteForm = (tenantId = "") => ({
  tenantId: String(tenantId || ""),
  templateId: QR_BUILDER_TEMPLATE_ID,
  status: "draft",
  name: DEFAULT_QR_NAME,
  title: DEFAULT_QR_TITLE,
  colors: { ...DEFAULT_QR_COLORS },
  assets: { ...DEFAULT_QR_ASSETS },
  text: normalizeTextMap({}),
})

export const cloneQrWebsiteConfig = config =>
  JSON.parse(JSON.stringify(config || createEmptyQrWebsiteForm()))

export const normalizeQrWebsiteBuilderForm = (input = {}, tenantId = "") => {
  const defaults = createEmptyQrWebsiteForm(tenantId)
  const colors = input.colors && typeof input.colors === "object" ? input.colors : {}
  const assets = input.assets && typeof input.assets === "object" ? input.assets : {}

  return {
    tenantId: String(tenantId || input.tenantId || defaults.tenantId || ""),
    templateId: resolveQrTemplateId(input.templateId),
    status: input.status === "published" ? "published" : "draft",
    name:
      typeof input.name === "string" && input.name.trim()
        ? input.name.trim()
        : defaults.name,
    title:
      typeof input.title === "string" && input.title.trim()
        ? input.title.trim()
        : defaults.title,
    colors: {
      primary: normalizeHexColor(colors.primary, defaults.colors.primary),
      secondary: normalizeHexColor(colors.secondary, defaults.colors.secondary),
    },
    assets: {
      logo:
        typeof assets.logo === "string" ? assets.logo.trim() : defaults.assets.logo,
      logoDark:
        typeof assets.logoDark === "string"
          ? assets.logoDark.trim()
          : defaults.assets.logoDark,
      favicon:
        typeof assets.favicon === "string"
          ? assets.favicon.trim()
          : defaults.assets.favicon,
      loginBackground:
        typeof assets.loginBackground === "string"
          ? assets.loginBackground.trim()
          : defaults.assets.loginBackground,
    },
    text: normalizeTextMap(input.text, defaults.text),
  }
}

export const setQrTextLang = (form, key, lang, value) => {
  const current = form?.text?.[key] || {}
  return {
    ...form,
    text: {
      ...form.text,
      [key]: {
        ...current,
        [lang]: value,
      },
    },
  }
}

export { getDeepValue, setDeepValue }
